import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getPaymentInfo,
  initiateGuestPayment,
  confirmGuestPayment,
  formatAmount,
} from '../services/guestPaymentService';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { isValidPhone } from '../utils/phone';
import './GuestPayment.css';

const GuestPayment = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [selectedGuestCount, setSelectedGuestCount] = useState(1);
  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [payerPhone, setPayerPhone] = useState('');
  // Per-field "missing required" flags for the payer info (name/email/phone).
  const [fieldErrors, setFieldErrors] = useState({});
  const [currency, setCurrency] = useState('EGP');
  const [processing, setProcessing] = useState(false);
  // Bank Misr / CyberSource Unified Checkout session:
  // { captureContext, clientLibrary, clientLibraryIntegrity, paymentId, amountCents }
  const [ucData, setUcData] = useState(null);

  // Check if this is a per-reservation payment (not per-guest)
  const isPerReservation = paymentInfo?.paymentType === 'per_reservation';

  useEffect(() => {
    fetchPaymentInfo();
  }, [token]);

  const fetchPaymentInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPaymentInfo(token);
      setPaymentInfo(data);

      // Set default guest count to remaining guests or 1
      if (data.remainingGuests > 0) {
        setSelectedGuestCount(Math.min(1, data.remainingGuests));
      }
    } catch (err) {
      console.error('Failed to fetch payment info', err);
      setError(err.response?.data?.message || 'Invalid payment link or link has expired');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    // Both per-guest and per-reservation now show the payer name/email/phone fields,
    // so require them in both flows.
    const missing = {
      payerName: !payerName.trim(),
      payerEmail: !payerEmail.trim(),
      payerPhone: !payerPhone.trim(),
    };
    if (missing.payerName || missing.payerEmail || missing.payerPhone) {
      setFieldErrors(missing);
      const labels = [];
      if (missing.payerName) labels.push('name');
      if (missing.payerEmail) labels.push('email');
      if (missing.payerPhone) labels.push('phone number');
      setError(`Please enter your ${labels.join(', ')}`);
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(payerEmail.trim())) {
      setFieldErrors({ payerEmail: true });
      setError('Please enter a valid email address');
      return;
    }
    if (!isValidPhone(payerPhone)) {
      setFieldErrors({ payerPhone: true });
      setError('Please enter a valid phone number for the selected country');
      return;
    }
    setFieldErrors({});

    try {
      setProcessing(true);
      setError(null);

      // For per-reservation, use totalGuests; for per-guest, use selectedGuestCount
      const guestCount = isPerReservation ? paymentInfo.totalGuests : selectedGuestCount;

      // Only send payer fields when provided. Per-reservation payments leave them
      // blank, and sending empty strings would fail the backend's optional @IsEmail
      // check; the backend falls back to the booking's own contact info.
      const payload = { guestCount, currency };
      if (payerName.trim()) payload.payerName = payerName.trim();
      if (payerEmail.trim()) payload.payerEmail = payerEmail.trim();
      if (payerPhone.trim()) payload.payerPhone = payerPhone.trim();

      const response = await initiateGuestPayment(token, payload);

      // Bank Misr (CyberSource Unified Checkout): render the embedded card form.
      if (response.gateway === 'bank_misr') {
        setUcData({
          captureContext: response.captureContext,
          clientLibrary: response.clientLibrary,
          clientLibraryIntegrity: response.clientLibraryIntegrity,
          paymentId: response.paymentId,
          amountCents: response.amountCents,
        });
        return;
      }

      // Paymob: full-page redirect to the Paymob-hosted checkout (browser leaves our
      // site). Apple Pay renders on Paymob's verified domain, and after payment Paymob
      // redirects the browser back to /guest-payment-success via the API callback.
      window.location.href = response.paymentUrl;
    } catch (err) {
      console.error('Failed to initiate payment', err);
      setError(err.response?.data?.message || 'Failed to initiate deposit. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Drive the CyberSource Unified Checkout SDK once a capture context is available.
  // Loads the SDK script (from the capture context), renders the embedded card form,
  // then exchanges the returned transient token for a confirmed payment.
  useEffect(() => {
    if (!ucData) return;
    let cancelled = false;

    const loadScript = (src, integrity) =>
      new Promise((resolve, reject) => {
        if (window.Accept) return resolve();
        const existing = document.querySelector('script[data-uc-sdk="1"]');
        if (existing) {
          existing.addEventListener('load', resolve);
          existing.addEventListener('error', () => reject(new Error('Failed to load payment SDK')));
          return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.setAttribute('data-uc-sdk', '1');
        if (integrity) {
          s.integrity = integrity;
          s.crossOrigin = 'anonymous';
        }
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Failed to load payment SDK'));
        document.body.appendChild(s);
      });

    const run = async () => {
      try {
        setError(null);
        await loadScript(ucData.clientLibrary, ucData.clientLibraryIntegrity);
        if (cancelled) return;
        if (!window.Accept) throw new Error('Payment form is unavailable. Please try again.');

        const accept = await window.Accept(ucData.captureContext);
        const unifiedPayments = await accept.unifiedPayments(false);
        const transientToken = await unifiedPayments.show({
          containers: {
            paymentSelection: '#uc-button-list',
            paymentScreen: '#uc-embedded',
          },
        });
        if (cancelled) return;

        setProcessing(true);
        const result = await confirmGuestPayment(token, {
          paymentId: ucData.paymentId,
          transientToken,
        });
        if (cancelled) return;

        if (result.success) {
          navigate(`/guest-payment-success?token=${token}&success=true`);
        } else {
          setError(result.message || 'Payment was declined. Please try again.');
          setUcData(null);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Unified Checkout error', err);
        setError(err.response?.data?.message || err.message || 'Payment failed. Please try again.');
        setUcData(null);
      } finally {
        if (!cancelled) setProcessing(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [ucData, token, navigate]);

  const calculateTotal = () => {
    if (!paymentInfo) return 0;
    // For per-reservation, return the remaining amount; for per-guest, calculate by guest count
    if (isPerReservation) {
      return paymentInfo.remainingAmountCents;
    }
    return selectedGuestCount * paymentInfo.pricePerGuestCents;
  };

  // Promo discount attributable to the current selection, for display only —
  // calculateTotal() is already net of it. Per-guest scales with how many guests
  // this payer is covering; per-reservation is the whole party's discount.
  const calculateDiscount = () => {
    if (!paymentInfo || !(paymentInfo.discountCents > 0)) return 0;
    if (isPerReservation) return paymentInfo.discountCents;
    return selectedGuestCount * (paymentInfo.discountPerGuestCents || 0);
  };

  // Service fee for the current selection. Must match the backend exactly:
  // fixed fee per guest + percentage of the subtotal. 0 when no fee is configured.
  const calculateFee = () => {
    if (!paymentInfo) return 0;
    const base = calculateTotal();
    const guestCount = isPerReservation ? paymentInfo.totalGuests : selectedGuestCount;
    const fixed = (paymentInfo.feeFixedCents || 0) * guestCount;
    const pct = Math.round((base * (paymentInfo.feePercent || 0)) / 100);
    return fixed + pct;
  };

  // What the customer actually pays = subtotal + service fee.
  const calculateGrandTotal = () => calculateTotal() + calculateFee();

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="guest-payment-container">
        <div className="loading-state">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading deposit information...</p>
        </div>
      </div>
    );
  }

  if (error && !paymentInfo) {
    return (
      <div className="guest-payment-container">
        <div className="error-state">
          <i className="bi bi-exclamation-circle"></i>
          <h2>Deposit Link Error</h2>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  if (ucData) {
    return (
      <div className="guest-payment-container">
        <div className="iframe-container">
          <div className="iframe-header">
            <button
              className="btn btn-outline-secondary"
              onClick={() => {
                setUcData(null);
                setError(null);
                fetchPaymentInfo(); // Refresh payment info
              }}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back
            </button>
            <h5>Complete Your Deposit</h5>
          </div>
          <div className="payment-iframe-wrapper" style={{ padding: '1rem' }}>
            {error && (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}
            <div id="uc-button-list"></div>
            <div id="uc-embedded" style={{ marginTop: '1rem' }}></div>
            {processing && (
              <div className="text-center mt-3">
                <span className="spinner-border spinner-border-sm me-2" />
                Processing your payment...
              </div>
            )}
          </div>
          <div className="iframe-footer">
            <div className="payment-secure-info">
              <i className="bi bi-shield-lock-fill"></i>
              Your deposit is secured by CyberSource (Bank Misr)
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if reservation is cancelled
  if (paymentInfo?.reservationStatus === 'cancelled') {
    return (
      <div className="guest-payment-container">
        <div className="error-state">
          <i className="bi bi-x-circle-fill"></i>
          <h2>Reservation Cancelled</h2>
          <p>This reservation has been cancelled. Deposits are no longer accepted.</p>
          <div className="reservation-summary">
            <div className="summary-item">
              <span className="label">Restaurant</span>
              <span className="value">{paymentInfo.restaurantName}</span>
            </div>
            <div className="summary-item">
              <span className="label">Date</span>
              <span className="value">{formatDate(paymentInfo.reservationDate)}</span>
            </div>
            <div className="summary-item">
              <span className="label">Time</span>
              <span className="value">{formatTime(paymentInfo.reservationTime)}</span>
            </div>
            <div className="summary-item">
              <span className="label">Guests</span>
              <span className="value">{paymentInfo.totalGuests}</span>
            </div>
          </div>
          <button className="btn btn-primary mt-3" onClick={() => navigate(`/widget?restaurantId=${paymentInfo.restaurantId}`)}>
            Make a New Reservation
          </button>
        </div>
      </div>
    );
  }

  // Check if reservation date is in the past
  if (paymentInfo?.reservationDate) {
    const resDate = new Date(paymentInfo.reservationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    resDate.setHours(0, 0, 0, 0);
    if (resDate < today) {
      return (
        <div className="guest-payment-container">
          <div className="error-state">
            <i className="bi bi-calendar-x-fill"></i>
            <h2>Reservation Date Has Passed</h2>
            <p>This reservation was for a past date. Deposits are no longer accepted.</p>
            <div className="reservation-summary">
              <div className="summary-item">
                <span className="label">Restaurant</span>
                <span className="value">{paymentInfo.restaurantName}</span>
              </div>
              <div className="summary-item">
                <span className="label">Date</span>
                <span className="value">{formatDate(paymentInfo.reservationDate)}</span>
              </div>
              <div className="summary-item">
                <span className="label">Time</span>
                <span className="value">{formatTime(paymentInfo.reservationTime)}</span>
              </div>
              <div className="summary-item">
                <span className="label">Guests</span>
                <span className="value">{paymentInfo.totalGuests}</span>
              </div>
            </div>
            <button className="btn btn-primary mt-3" onClick={() => navigate(`/widget?restaurantId=${paymentInfo.restaurantId}`)}>
              Make a New Reservation
            </button>
          </div>
        </div>
      );
    }
  }

  if (paymentInfo?.isFullyPaid) {
    return (
      <div className="guest-payment-container">
        <div className="success-state">
          <i className="bi bi-check-circle-fill"></i>
          <h2>Deposit Complete!</h2>
          <p>This reservation's deposit has been fully paid.</p>
          <div className="reservation-summary">
            <div className="summary-item">
              <span className="label">Restaurant</span>
              <span className="value">{paymentInfo.restaurantName}</span>
            </div>
            <div className="summary-item">
              <span className="label">Date</span>
              <span className="value">{formatDate(paymentInfo.reservationDate)}</span>
            </div>
            <div className="summary-item">
              <span className="label">Time</span>
              <span className="value">{formatTime(paymentInfo.reservationTime)}</span>
            </div>
            <div className="summary-item">
              <span className="label">Guests</span>
              <span className="value">{paymentInfo.totalGuests}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="guest-payment-container">
      <div className="payment-card">
        {/* Banner: the restaurant image, full-width at the top of the card */}
        {paymentInfo?.restaurantLogo && (
          <img
            src={paymentInfo.restaurantLogo.startsWith('http')
              ? paymentInfo.restaurantLogo
              : `${process.env.REACT_APP_BACKEND_URL || ''}${paymentInfo.restaurantLogo}`}
            alt={paymentInfo.restaurantName}
            className="payment-banner"
          />
        )}
        {/* Header */}
        <div className="payment-header">
          <h1>{paymentInfo?.restaurantName}</h1>
          <p className="reservation-for">Reservation for {paymentInfo?.guestName}</p>
        </div>

        {/* Reservation Details */}
        <div className="reservation-details">
          <div className="detail-item">
            <i className="bi bi-calendar-event"></i>
            <span>{formatDate(paymentInfo?.reservationDate)}</span>
          </div>
          <div className="detail-item">
            <i className="bi bi-clock"></i>
            <span>{formatTime(paymentInfo?.reservationTime)}</span>
          </div>
          <div className="detail-item">
            <i className="bi bi-people"></i>
            <span>{paymentInfo?.totalGuests} guests</span>
          </div>
        </div>

        {/* Payment Progress */}
        <div className="payment-progress">
          <h4>Deposit Progress</h4>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{
                width: `${((paymentInfo?.totalPaidCents || 0) / (paymentInfo?.totalRequiredCents || 1)) * 100}%`,
              }}
            />
          </div>
          <div className="progress-stats">
            {!isPerReservation && (
              <span>
                <strong>{paymentInfo?.paidGuestCount}</strong> of{' '}
                <strong>{paymentInfo?.totalGuests}</strong> guests paid
              </span>
            )}
            <span>
              EGP {formatAmount(paymentInfo?.totalPaidCents || 0)} / EGP{' '}
              {(paymentInfo?.discountCents || 0) > 0 && (
                <span style={{ textDecoration: 'line-through', color: '#94a3b8', marginRight: 6 }}>
                  {formatAmount(paymentInfo?.originalTotalRequiredCents || 0)}
                </span>
              )}
              {formatAmount(paymentInfo?.totalRequiredCents || 0)}
            </span>
          </div>
        </div>

        {/* Confirmation Hint */}
        <div className="payment-hint">
          <i className="bi bi-info-circle"></i>
          <span>Your reservation will be confirmed once the full deposit is complete. Thank you!</span>
        </div>

        {/* Payment History */}
        {paymentInfo?.payments?.length > 0 && (
          <div className="payment-history">
            <h4>Deposit History</h4>
            <div className="history-list">
              {paymentInfo.payments
                .filter((p) => p.status === 'paid')
                .map((payment) => (
                  <div key={payment.id} className="history-item">
                    <div className="history-info">
                      <span className="payer-name">
                        {payment.payerName || 'Guest'}
                      </span>
                      <span className="guest-count">
                        {payment.guestCount} guest{payment.guestCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <span className="amount">EGP {formatAmount(payment.amountCents)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Pay Form - Per Reservation: Simple pay button */}
        {isPerReservation && paymentInfo?.remainingAmountCents > 0 && (
          <div className="pay-form">
            {error && (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {/* Payer Info — required (same as the per-guest flow) */}
            <div className="form-group">
              <label>Your Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-control ${fieldErrors.payerName ? 'is-invalid' : ''}`}
                value={payerName}
                onChange={(e) => {
                  setPayerName(e.target.value);
                  if (e.target.value.trim()) setFieldErrors((p) => ({ ...p, payerName: false }));
                }}
                placeholder="Enter your name"
                required
              />
            </div>

            <div className="form-group">
              <label>Your Email <span className="text-danger">*</span></label>
              <input
                type="email"
                className={`form-control ${fieldErrors.payerEmail ? 'is-invalid' : ''}`}
                value={payerEmail}
                onChange={(e) => {
                  setPayerEmail(e.target.value);
                  if (e.target.value.trim()) setFieldErrors((p) => ({ ...p, payerEmail: false }));
                }}
                placeholder="Enter your email"
                required
              />
            </div>

            <div className="form-group">
              <label>Your Phone <span className="text-danger">*</span></label>
              <PhoneInput
                country={'eg'}
                value={payerPhone}
                onChange={(phone) => {
                  setPayerPhone('+' + phone);
                  if (phone.trim()) setFieldErrors((p) => ({ ...p, payerPhone: false }));
                }}
                inputProps={{ required: true }}
                containerClass="phone-input-container"
                inputClass={`form-control ${fieldErrors.payerPhone ? 'is-invalid' : ''}`}
                buttonClass="phone-input-button"
                enableSearch={true}
                searchPlaceholder="Search country..."
                preferredCountries={['eg', 'sa', 'ae', 'us', 'gb']}
              />
            </div>

            {/* Payment method selector removed — regular reservation payments are
                EGP/National only. International (USD) cards apply to Experiences. */}

            {/* Total (subtotal + service fee when a fee is configured) */}
            <div className="total-section">
              {calculateFee() > 0 && (
                <div className="total-amount" style={{ fontWeight: 400 }}>
                  <span>Subtotal:</span>
                  <span className="amount">EGP {formatAmount(calculateTotal())}</span>
                </div>
              )}
              {calculateDiscount() > 0 && (
                <div className="total-amount" style={{ fontWeight: 400, color: '#166534' }}>
                  <span>Promo discount applied</span>
                  <span className="amount">− EGP {formatAmount(calculateDiscount())}</span>
                </div>
              )}
              {calculateFee() > 0 && (
                <div className="total-amount" style={{ fontWeight: 400 }}>
                  <span>Service fee:</span>
                  <span className="amount">EGP {formatAmount(calculateFee())}</span>
                </div>
              )}
              <div className="total-amount">
                <span>Total:</span>
                <span className="amount">EGP {formatAmount(calculateGrandTotal())}</span>
              </div>
            </div>

            {/* Pay Button */}
            <button
              className="btn btn-primary btn-pay"
              onClick={handlePayment}
              disabled={processing}
            >
              {processing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-credit-card me-2"></i>
                  Deposit EGP {formatAmount(calculateGrandTotal())}
                </>
              )}
            </button>
          </div>
        )}

        {/* Pay Form - Per Guest: Full form with guest selector and payer info */}
        {!isPerReservation && paymentInfo?.remainingGuests > 0 && (
          <div className="pay-form">
            <h4>Make a Deposit For:</h4>

            {error && (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {/* Guest Count Selector */}
            <div className="form-group">
              <div className="guest-selector">
                {[...Array(paymentInfo.remainingGuests)].map((_, index) => (
                  <button
                    key={index + 1}
                    type="button"
                    className={`guest-count-btn ${
                      selectedGuestCount === index + 1 ? 'active' : ''
                    }`}
                    onClick={() => setSelectedGuestCount(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>

            {/* Payer Info — required */}
            <div className="form-group">
              <label>Your Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-control ${fieldErrors.payerName ? 'is-invalid' : ''}`}
                value={payerName}
                onChange={(e) => {
                  setPayerName(e.target.value);
                  if (e.target.value.trim()) setFieldErrors((p) => ({ ...p, payerName: false }));
                }}
                placeholder="Enter your name"
                required
              />
            </div>

            <div className="form-group">
              <label>Your Email <span className="text-danger">*</span></label>
              <input
                type="email"
                className={`form-control ${fieldErrors.payerEmail ? 'is-invalid' : ''}`}
                value={payerEmail}
                onChange={(e) => {
                  setPayerEmail(e.target.value);
                  if (e.target.value.trim()) setFieldErrors((p) => ({ ...p, payerEmail: false }));
                }}
                placeholder="Enter your email"
                required
              />
            </div>

            <div className="form-group">
              <label>Your Phone <span className="text-danger">*</span></label>
              <PhoneInput
                country={'eg'}
                value={payerPhone}
                onChange={(phone) => {
                  setPayerPhone('+' + phone);
                  if (phone.trim()) setFieldErrors((p) => ({ ...p, payerPhone: false }));
                }}
                inputProps={{ required: true }}
                containerClass="phone-input-container"
                inputClass={`form-control ${fieldErrors.payerPhone ? 'is-invalid' : ''}`}
                buttonClass="phone-input-button"
                enableSearch={true}
                searchPlaceholder="Search country..."
                preferredCountries={['eg', 'sa', 'ae', 'us', 'gb']}
              />
            </div>

            {/* Payment method selector removed — regular reservation payments are
                EGP/National only. International (USD) cards apply to Experiences. */}

            {/* Total */}
            <div className="total-section">
              <div className="total-breakdown">
                <span>
                  {selectedGuestCount} guest{selectedGuestCount !== 1 ? 's' : ''} × EGP{' '}
                  {calculateDiscount() > 0 && (
                    <span style={{ textDecoration: 'line-through', color: '#94a3b8', marginRight: 6 }}>
                      {formatAmount(paymentInfo.originalPricePerGuestCents)}
                    </span>
                  )}
                  {formatAmount(paymentInfo.pricePerGuestCents)}
                </span>
              </div>
              {calculateFee() > 0 && (
                <div className="total-amount" style={{ fontWeight: 400 }}>
                  <span>Subtotal:</span>
                  <span className="amount">EGP {formatAmount(calculateTotal())}</span>
                </div>
              )}
              {calculateDiscount() > 0 && (
                <div className="total-amount" style={{ fontWeight: 400, color: '#166534' }}>
                  <span>Promo discount applied</span>
                  <span className="amount">− EGP {formatAmount(calculateDiscount())}</span>
                </div>
              )}
              {calculateFee() > 0 && (
                <div className="total-amount" style={{ fontWeight: 400 }}>
                  <span>Service fee:</span>
                  <span className="amount">EGP {formatAmount(calculateFee())}</span>
                </div>
              )}
              <div className="total-amount">
                <span>Total:</span>
                <span className="amount">EGP {formatAmount(calculateGrandTotal())}</span>
              </div>
            </div>

            {/* Pay Button */}
            <button
              className="btn btn-primary btn-pay"
              onClick={handlePayment}
              disabled={processing || selectedGuestCount < 1}
            >
              {processing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="bi bi-credit-card me-2"></i>
                  Deposit EGP {formatAmount(calculateGrandTotal())}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestPayment;
